// SINGLE SOURCE for CLIENT INTERNAL AUDIT quotation content — used by the office preview, the client signing page
// and the quotation email, so they can never disagree. bizSAFE quotations do NOT use this file.
import { format, parseISO } from 'date-fns'
import { standardsLabel } from './service'

// Standards on an IA quote: the reminder form stores ia_standards (array); the first-time enquiry form stores the
// ticked boxes in ia_standards (+ free text in ia_standards_other).
export function iaQuoteStandards(quote) {
  const r = quote?.form_responses || {}
  const list = Array.isArray(r.ia_standards) ? r.ia_standards
    : (typeof r.ia_standards === 'string' && r.ia_standards ? r.ia_standards.split(/\s*[,;]\s*/) : [])
  const clean = list.filter(s => s && !/^other/i.test(s))
  if ((r.ia_standards_other || '').trim()) clean.push(r.ia_standards_other.trim())
  return clean
}

export const IA_PAYMENT_TERMS = '100% upfront payment\nPayNow / GIRO preferred — UEN 199205611D'
export const IA_REMARK = 'Please ensure all management system documents and records are complete and available for review before the audit date.'

const mdText = (m) => { const n = parseFloat(m); if (!n) return 'X man-day'; return `${n} man-day${n === 1 ? '' : 's'}` }

// Multi-year contract: 1 internal audit a year, invoiced each year. 1 = a single audit (the default).
export const IA_CONTRACT_OPTIONS = [1, 2, 3]
export const iaContractYears = (quote) => Math.max(1, parseInt(quote?.ia_contract_years, 10) || 1)

// Fee wording: "S$1,200.00 w/o GST (…w/ 9% GST) per audit" + for a contract "3 audits over 3 years — S$3,600.00 w/o
// GST in total". fmt = the page's money formatter.
export function iaFeeText(quote, fmt) {
  const years = iaContractYears(quote)
  const base = `${fmt(quote?.fee_ex_gst)} w/o GST\n(${fmt(quote?.fee_inc_gst)} w/ 9% GST) per audit`
  if (years === 1) return base
  return `${base}\n${years} audits over ${years} years — ${fmt((parseFloat(quote?.fee_ex_gst) || 0) * years)} w/o GST in total`
}

// [label, value] rows for the quotation body (fee row is rendered separately by each page).
export function iaQuoteRows(quote, client) {
  const r = quote?.form_responses || {}
  const businessNature = r.business_nature || client?.notes || client?.industry || ''
  const expiry = r.iso_cert_expiry
  const years = iaContractYears(quote)
  return [
    ['Scope of service', `Third-Party Internal Audit (${mdText(quote?.mandays)}${years > 1 ? ' per audit' : ''})\n• To assess the effectiveness of implementation (sampling)\n• Internal Audit report shall be issued by CCIS`],
    years > 1 ? ['Contract period', `${years} years — 1 internal audit per year, invoiced yearly`] : null,
    ['Standard(s)', standardsLabel(iaQuoteStandards(quote)) || '—'],
    businessNature ? ['Business nature', businessNature] : null,
    ['Current cert expiry', expiry ? format(parseISO(String(expiry).slice(0, 10)), 'd MMMM yyyy') : '—'],
    ['Payment terms', IA_PAYMENT_TERMS],
    ['Remarks', `• ${IA_REMARK}${quote?.remarks ? '\n• ' + quote.remarks : ''}`],
  ].filter(Boolean)
}

export function iaQuoteTitle(quote) {
  const s = standardsLabel(iaQuoteStandards(quote))
  const years = iaContractYears(quote)
  return `${s ? s + ' — ' : ''}Third-Party Internal Audit${years > 1 ? ` (${years}-year contract)` : ''}`
}

// Client email (approved wording). Returned as plain text for textEmailHtml (link on its own line → button).
export function iaQuoteEmail({ company, contact, quoteRef, link, validityDays }) {
  return {
    subject: `${company} - Third-Party Internal Audit Quotation ${quoteRef}`,
    body: `Dear ${contact || 'Sir/Madam'},

Thank you for your interest in our Third-Party Internal Audit.

Please view and accept our quotation here:
${link}

This quotation is valid for ${validityDays || 30} days. Feel free to contact us should you require any clarification, thank you.

Best regards,
CCIS Singapore Pte Ltd
(65) 6376 0550`,
  }
}

// The two clauses of the internal audit terms (the consultancy "twelve months / certification" clause is removed).
export const IA_TERMS = [
  'For postponements of any pre-agreed session by the client, an abortive charge of S$545 w/ GST shall be applied. The abortive charge is not applicable should the client notify CCIS of the postponement / changes at least 7 working days in advance from the agreed date of session.',
  'The client shall pay CCIS on-time and in strict accordance with the payment schedule. Late interest charge of 2% per month or part thereof shall be levied on any outstanding amount. No cancellation is allowed - The client shall compensate CCIS for loss or damage resulting from holding, suspending or cancellation of this service agreement. The company’s liability shall include (but is not limited to) fees for services that have been performed and 100% of the contractual sum for the services not yet performed. Any legal fees incurred by CCIS for recovery purpose shall be borne by the client, including a strictly non-waivable administrative fee of S$1,000 once we submit the case to The Small Claims Tribunals.',
]
